import { useState } from "react";
import { Play } from "lucide-react";
import { track } from "../analytics";

// Promo video shown on the homepage between the Hero and Features. Renders a
// lightweight click-to-play facade (thumbnail + play button) rather than an
// eager <iframe>, so the ~1MB+ YouTube player and its cookies are only loaded
// once the visitor actually wants to watch. Keeps the SSR page fast and avoids
// a cookie-consent footgun. Uses youtube-nocookie.com for the same reason.

const VIDEO_ID = "Otk7FbND2AQ";
// hqdefault is the only thumbnail size guaranteed to exist for every video
// (maxresdefault 404s for some uploads); it's 480x360, plenty at this scale.
const THUMBNAIL = `https://i.ytimg.com/vi/${VIDEO_ID}/hqdefault.jpg`;

export function VideoDemo() {
  const [playing, setPlaying] = useState(false);

  const play = () => {
    track("promo_video_play", { video_id: VIDEO_ID, from: "home" });
    setPlaying(true);
  };

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900 mb-4">
            See OmniCanvas in action
          </h2>
          <p className="text-lg text-slate-600">
            Watch notes, images, sketches, web clips, and optional meeting
            capture come together in one visual workspace.
          </p>
        </div>

        <div className="relative rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/50 overflow-hidden">
          {/* Window chrome — matches the CanvasDemo frame */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-slate-50/80">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-amber-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
            <span className="ml-3 text-xs text-slate-400 font-medium">
              OmniCanvas — Demo
            </span>
          </div>

          {/* 16:9 video area */}
          <div className="relative aspect-video bg-slate-900">
            {playing ? (
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${VIDEO_ID}?autoplay=1&rel=0`}
                title="OmniCanvas product demo"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                type="button"
                onClick={play}
                aria-label="Play the OmniCanvas demo video"
                className="group absolute inset-0 w-full h-full cursor-pointer"
              >
                <img
                  src={THUMBNAIL}
                  alt="OmniCanvas product demo"
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span className="absolute inset-0 bg-slate-900/20 group-hover:bg-slate-900/30 transition-colors" />
                <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-900/30 group-hover:bg-indigo-700 group-hover:scale-105 transition-all">
                  <Play size={28} className="ml-1 fill-current" />
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
